import React, { useState, useEffect } from 'react';
import { Settings, Eye, EyeOff, Save, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    show_prices: true,
    show_stock: true
  });
  const [originalSettings, setOriginalSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setOriginalSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: "Error",
        description: "Failed to load settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOriginalSettings(settings);
      toast({
        title: "Success",
        description: "Display settings updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  if (loading) {
    return (
      <div className="p-4 sm:p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-800">Site Settings</h1>
          <p className="text-slate-600 mt-1 text-sm sm:text-base">Control what customers see on the website</p>
        </div>
        <Button
          onClick={handleSaveSettings}
          disabled={!hasChanges || saving}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">About Display Settings</p>
            <p className="mt-1">
              These settings control what information is visible to customers on the public website. 
              Changes will take effect immediately across all pages including the Home page, Category pages, and Product listings.
            </p>
          </div>
        </div>
      </div>

      {/* Display Settings Card */}
      <Card className="border-2 max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-purple-600" />
            Product Display Settings
          </CardTitle>
          <CardDescription>
            Choose what product information is shown to customers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Show Prices Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-full ${settings.show_prices ? 'bg-green-100' : 'bg-slate-200'}`}>
                {settings.show_prices ? (
                  <Eye className="h-5 w-5 text-green-600" />
                ) : (
                  <EyeOff className="h-5 w-5 text-slate-500" />
                )}
              </div>
              <div>
                <Label htmlFor="show-prices" className="text-base font-medium text-slate-800 cursor-pointer">
                  Show Product Prices
                </Label>
                <p className="text-sm text-slate-500 mt-1">
                  {settings.show_prices 
                    ? 'Prices are visible to all customers on the website' 
                    : 'Prices are hidden from customers (they can request quotes)'}
                </p>
              </div>
            </div>
            <Switch
              id="show-prices"
              checked={settings.show_prices}
              onCheckedChange={(checked) => setSettings({...settings, show_prices: checked})}
            />
          </div>

          {/* Show Stock Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-full ${settings.show_stock ? 'bg-green-100' : 'bg-slate-200'}`}>
                {settings.show_stock ? (
                  <Eye className="h-5 w-5 text-green-600" />
                ) : (
                  <EyeOff className="h-5 w-5 text-slate-500" />
                )}
              </div>
              <div>
                <Label htmlFor="show-stock" className="text-base font-medium text-slate-800 cursor-pointer">
                  Show Stock Levels
                </Label>
                <p className="text-sm text-slate-500 mt-1">
                  {settings.show_stock 
                    ? 'Stock quantities are visible to customers' 
                    : 'Stock levels are hidden (only "In Stock" or "Out of Stock" shown)'}
                </p>
              </div>
            </div>
            <Switch
              id="show-stock"
              checked={settings.show_stock}
              onCheckedChange={(checked) => setSettings({...settings, show_stock: checked})}
            />
          </div>

          {/* Preview */}
          <div className="border-t pt-6">
            <h4 className="font-medium text-slate-800 mb-3">Preview</h4>
            <div className="bg-white border-2 rounded-lg p-4 max-w-xs">
              <div className="aspect-square bg-slate-100 rounded-lg mb-3 flex items-center justify-center">
                <span className="text-slate-400 text-sm">Product Image</span>
              </div>
              <h5 className="font-semibold text-slate-800 text-sm mb-2">Sample Product Name</h5>
              {settings.show_prices ? (
                <p className="text-lg font-bold text-purple-600">$29.99</p>
              ) : (
                <p className="text-sm text-slate-500 italic">Contact for pricing</p>
              )}
              {settings.show_stock ? (
                <p className="text-sm text-green-600 mt-1">45 in stock</p>
              ) : (
                <p className="text-sm text-green-600 mt-1">In Stock</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSettings;
